const ADMIN_SESSION_PLACEHOLDER_TOKEN = 'cookie-session';

const ROLE_PERMISSIONS = {
  viewer: ['dashboard:read', 'health:read', 'products:read', 'spareparts:read', 'requests:read', 'users:read', 'team:read'],
  editor: ['dashboard:read', 'health:read', 'products:read', 'products:write', 'spareparts:read', 'spareparts:write', 'requests:read', 'requests:write', 'users:read', 'team:read', 'team:write', 'uploads:write', 'audit:read'],
  admin: [
    'dashboard:read',
    'health:read',
    'products:read',
    'products:write',
    'products:delete',
    'spareparts:read',
    'spareparts:write',
    'spareparts:delete',
    'requests:read',
    'requests:write',
    'requests:delete',
    'users:read',
    'users:write',
    'users:delete',
    'team:read',
    'team:write',
    'team:delete',
    'uploads:write',
    'audit:read',
    'audit:delete',
    'settings:write',
  ],
};

function getPermissionsForRole(role) {
  const normalized = String(role || '').trim().toLowerCase();
  return ROLE_PERMISSIONS[normalized] || ROLE_PERMISSIONS.admin;
}

function normalizeAdminRoleKey(roleKey, role) {
  const normalizedRoleKey = String(roleKey || '').trim().toLowerCase();
  if (ROLE_PERMISSIONS[normalizedRoleKey]) {
    return normalizedRoleKey;
  }

  const normalizedRole = String(role || '').trim().toLowerCase();
  return ROLE_PERMISSIONS[normalizedRole] ? normalizedRole : '';
}

function normalizeAdminSessionPayload(payload = {}) {
  const roleKey = normalizeAdminRoleKey(payload.roleKey, payload.role) || 'admin';
  const sessionExpiresAt = Number(payload.sessionExpiresAt || 0);
  const sessionVersion = Number(payload.sessionVersion || 0);
  return {
    token: ADMIN_SESSION_PLACEHOLDER_TOKEN,
    role: String(payload.role || '').trim() || 'Admin',
    roleKey,
    permissions: getPermissionsForRole(roleKey),
    payload,
    sessionExpiresAt: Number.isFinite(sessionExpiresAt) && sessionExpiresAt > 0 ? sessionExpiresAt : null,
    sessionVersion: Number.isFinite(sessionVersion) && sessionVersion >= 0 ? sessionVersion : 0,
  };
}

function readStoredAdminProfile() {
  if (typeof window === 'undefined') return null;

  const storedValues = [];
  try {
    storedValues.push(sessionStorage.getItem('admin_profile'));
  } catch (err) {
    storedValues.push(null);
  }
  try {
    storedValues.push(localStorage.getItem('admin_profile'));
  } catch (err) {
    storedValues.push(null);
  }

  for (const rawProfile of storedValues) {
    if (!rawProfile) continue;
    try {
      const parsed = JSON.parse(rawProfile);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (err) {
      // Ignore malformed stored profiles and try the next source.
    }
  }

  return null;
}

export function decodeAdminToken(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) return null;
    const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded));
    return payload && typeof payload === 'object' ? payload : null;
  } catch (err) {
    return null;
  }
}

export function getAdminSessionFromStorage() {
  if (typeof window === 'undefined') return null;
  const payload = readStoredAdminProfile();
  if (!payload) return null;
  return normalizeAdminSessionPayload(payload);
}

export async function fetchAdminSessionFromServer() {
  if (typeof window === 'undefined') return null;
  try {
    const res = await fetch('/api/admin/session', {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      return normalizeAdminSessionPayload(data?.session || data?.admin || data);
    }

    if (res.status === 401 || res.status === 403) {
      return null;
    }
  } catch (err) {
    // Fall back to stored state if the session endpoint is temporarily unreachable.
  }

  return getAdminSessionFromStorage();
}

export function hasStoredAdminSession() {
  return Boolean(getAdminSessionFromStorage());
}

export function adminHasPermission(session, permission) {
  if (!session) return false;
  if (!permission) return true;
  return Array.isArray(session.permissions) && session.permissions.includes(permission);
}

export function getAdminPermissionFlags(session, permissionMap = {}) {
  return Object.entries(permissionMap || {}).reduce((acc, [key, permission]) => {
    acc[key] = adminHasPermission(session, permission);
    return acc;
  }, {});
}

export { getPermissionsForRole, normalizeAdminSessionPayload };
